/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { QuestionResolver } from 'app/core/components/assessments/question-resolver';
import { IQuestionComponent, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { fromApiDecimal } from 'app/shared/models/essette-types';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import { guid } from 'app/shared/lib/guid';
import * as Core from 'app/core/models/essette-api';


@autoinject
export class QuestionCheckboxCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////

    protected selectedChoices: Core.AssessmentChoice[] = [];
    protected matcher = modelIdMatcher;
    protected id: guid = guid();
    protected ruleID: string = `question-rule-${this.id}`;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly element: Element,
        protected readonly context: AssessmentContext,
        protected readonly resolver: QuestionResolver,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.labelId = config.labelId;

        if(!this.resolver.isInitialized) { await this.resolver.init(); }
    }

    protected attached(): void {
        this.loadPreviousAnswer();
        this.updateScore();
    }


    ////****************** Events ***********************************************////
    protected choicesChanged() {
        this.isValid = this.selectedChoices.length > 0;

        if(this.context.attachment.isScored) {
            this.updateScore();
        }
    }


    ////****************** Functions ****************************************************////
    private async loadPreviousAnswer(): Promise<void> {
        const questionId = this.question && this.question.id;
        if(!questionId || !this.context.attachment.currentAnswers) { return; }

        const description = this.question && this.question.description || '';

        try {
            // Filter to current answers to only ones that belong to this question, then set selectedChoices to the choice for those answers
            const answers = this.context.attachment.currentAnswers.filter(a => {
                if(!a.question) { return false; }

                if(!this.question.sharedQuestion) {
                    return a.question.id === this.question.id;
                } else if(!!this.question.sharedQuestion && !!a.sharedQuestion) {
                    return this.question.sharedQuestion.id === a.sharedQuestion.id;
                }

                return false;
            });

            // Loop through each choice for the question and see if it exists in current answers and add it to selectedChoices
            if(this.question && this.question.choices) {
                this.question.choices.forEach(c => {
                    answers.forEach(a => {
                        if(!c.sharedChoice && a.choice && c.id === a.choice.id) {
                            this.selectedChoices.push(c);
                        } else if(!!c.sharedChoice && !!a.sharedChoice && c.sharedChoice.id === a.sharedChoice.id) {
                            this.selectedChoices.push(c);
                        }
                    });
                });
            }
        }
        catch(error) {
            this.logger.error(`Unable to find existing answer(s) for question ${questionId} (${description})`);
            throw error;
        }
    }

    private updateScore(): void {
        if(!!this.selectedChoices && this.selectedChoices.length > 0 && !!this.element) {
            let score: number = 0.00;
            this.selectedChoices.forEach(c => {
                score += fromApiDecimal(c.score) || 0;
            })
            const { question } = this;
            this.element.dispatchEvent(new CustomEvent('score-change', { bubbles: true, detail: { questionId: question.id, score: score } }));
        }
    }

    ////****************** IQuestionComponent ****************************************************////
    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Core.AssessmentAnswer[] {
        if(this.selectedChoices && this.selectedChoices.length > 0) {
            const answers = this.selectedChoices.map(c => {
                return {
                    question: { id: c.question && c.question.id || null },
                    choice: { id: c.id }
                } as Core.AssessmentAnswer
            });

            return answers;
        }

        return [];
    }

}
